import { Component, Input, Output, EventEmitter } from '@angular/core';
import { ChatbotService } from '../../../services/chatbot-services/chatbot.service';
import { LoggerService } from '../../../services/logger-services/logger.service';

/**
 * ShareConversationComponent handles the sharing functionality for chat conversations
 * Generates share links, manages share popups, and handles clipboard operations
 */
@Component({
  selector: 'app-share-conversation',
  templateUrl: './share-conversation.component.html',
  styleUrls: ['./share-conversation.component.scss']
})
export class ShareConversationComponent {
  /** Chat data object containing conversation information to be shared */
  @Input() chatdata: any;
  
  /** Tenant ID for the current user/organization */
  @Input() tenantId: string = '';
  @Input() isAgent: boolean = false;
  /** Event emitter for share response data */
  @Output() shareResponse = new EventEmitter<any>();
  
  /** Event emitter to show share popup with share link data */
  @Output() showSharePopup = new EventEmitter<any>();
  
  /** Event emitter to hide share popup */
  @Output() hideSharePopup = new EventEmitter<void>();
  
  /** Event emitter for universal loading state management */
  @Output() universalLoading = new EventEmitter<boolean>();

  /** Internal state for currently selected item being shared */
  selectedItemForAction: any = null;
  
  /** Generated share link URL */
  shareLink: string = '';

  /**
   * Error formatting function for consistent error logging
   * @param error - The error object to format
   * @returns Formatted error string with component identifier
   */
  private readonly ShareConversationError = (error: any) => 
    `['ShareConversation'] - ${JSON.stringify(error)}`;

  /**
   * Constructor - Initializes component with required services
   * @param chatbotService - Service for chatbot operations and share API calls
   * @param loggerService - Service for error logging and debugging
   */
  constructor(
    private readonly chatbotService: ChatbotService,
    private readonly loggerService: LoggerService
  ) { }

  /**
   * Initiates the conversation sharing process
   * Entry point for sharing functionality - validates data and triggers share operation
   * @returns void
   */
  shareConversation(): void {
    try {
      // Directly proceed with sharing - no confirmation popup
      this.performShareConversation();
    } catch (error: any) {
      this.loggerService.logError(this.ShareConversationError(error), "shareConversation");
    }
  }

  /**
   * Performs the actual conversation sharing operation
   * Makes API call to generate share link and handles response processing
   * @private
   * @returns void
   */
  private performShareConversation(): void {
    try {
      // Guard clause for null/undefined chatdata
      if (!this.chatdata || !this.chatdata.pk_chathistoryid) {
        this.loggerService.logError("Invalid chatdata provided for sharing", "performShareConversation");
        return;
      }

      // Emit loading state to parent component
      this.universalLoading.emit(true);
      
      // Generate a new share link every time
      this.chatbotService.shareConversation('share_link', this.tenantId, this.chatdata.pk_chathistoryid).subscribe({
        next: (response: any) => {
          this.universalLoading.emit(false);
          
          // Process successful response
          if (response.body && response.body.status === 'Success') {
            const shareLinkId = response.body.share_link;
            
            if (shareLinkId) {
              // Construct full share URL with origin and query parameters
              this.shareLink = `${window.location.origin}/shared-chat/${shareLinkId}?tenant_id=${this.tenantId}&response_id=${this.chatdata.pk_chathistoryid}`;
              
              // Prepare data for share popup
              this.selectedItemForAction = {
                share_link: this.shareLink,
                response_id: this.chatdata.response_id,
                tenant_id: this.tenantId
              };
              
              // Emit events to parent component
              this.showSharePopup.emit(this.selectedItemForAction);
              this.shareResponse.emit(response);
            } else {
              this.loggerService.logError("No share link ID received from API", "performShareConversation");
            }
          } else {
            this.loggerService.logError(`API returned unsuccessful status: ${response.body?.status}`, "performShareConversation");
          }
        },
        error: (error: any) => {
          this.universalLoading.emit(false);
          this.loggerService.logError(`Error in performShareConversation: ${JSON.stringify(error?.message)}`, "performShareConversation");
        }
      });
    } catch (error: any) {
      this.universalLoading.emit(false);
      this.loggerService.logError(this.ShareConversationError(error), "performShareConversation");
    }
  }

  /**
   * Copies the share link to the system clipboard
   * Uses the modern Clipboard API with fallback error handling
   * @param item - Object containing the share_link property to be copied
   * @returns void
   */
  copyShareLink(item: any): void {
    try {
      // Validate that item contains a share link
      if (!item || !item.share_link) {
        this.loggerService.logError("No share link provided for copying", "copyShareLink");
        return;
      }

      // Use modern Clipboard API to copy share link
      navigator.clipboard.writeText(item.share_link).then(() => {
        // Hide popup after successful copy operation
        this.hideSharePopup.emit();
      }).catch(err => {
        this.loggerService.logError(`Error in copyShareLink: ${JSON.stringify(err?.message)}`, "copyShareLink");
        // Could implement fallback copy method here if needed
      });
    } catch (error: any) {
      this.loggerService.logError(this.ShareConversationError(error), "copyShareLink");
    }
  }
  
  /**
   * Closes the share popup dialog
   * Emits event to parent component to hide the share popup
   * @returns void
   */
  closeSharePopup(): void {
    try {
      this.hideSharePopup.emit();
    } catch (error: any) {
      this.loggerService.logError(this.ShareConversationError(error), "closeSharePopup");
    }
  }
}